/**
 * Julkaistun Appin SISÄÄNRAKENNETUT tekstit (WP-VM1,
 * app-builder-validointiviestit-plan.md).
 *
 * <p>Nämä ovat ne tekstit joita Appin tekijä ei voi asettaa proppina: lomakkeen
 * validointivirheet ja latausilmoitus. Ennen tätä moduulia ne olivat
 * kovakoodattuja englanninkielisiä merkkijonoja kahdessatoista kohdassa
 * (`runtime.ts`, `formValidation.ts`), jolloin täysin suomenkielinen App näytti
 * `Required`-virheen eikä sitä voinut korjata millään.
 *
 * <p><b>Prioriteetti</b> (ylin voittaa):
 * <pre>
 *   säännön oma message  >  metadata.messages[avain]  >  metadata.locale -katalogi  >  en
 * </pre>
 *
 * <p><b>Ei i18next-riippuvuutta.</b> Runtime on kehysriippumaton bundle
 * sandbox-iframessa; portaalin i18n lokalisoi editorin, ei ajettavaa Appia.
 * Katalogi on siksi runtimen omaa dataa — kaksitoista tekstiä kahdella kielellä.
 */

/** Sisäänrakennetun tekstin avain. Uusi teksti = uusi avain + molemmat katalogit. */
export type MessageKey =
  | 'required'
  | 'notANumber'
  | 'min'
  | 'max'
  | 'minLength'
  | 'maxLength'
  | 'regex'
  | 'email'
  | 'invalid'
  | 'loading'
  // WP-KL3 (löydös E-3): tekstit joita runtime kirjoittaa DATAAN, ei lomakkeen
  // virheisiin. Nämä olivat kovakoodattua englantia myös suomenkielisessä Appissa.
  | 'booleanTrue'
  | 'booleanFalse'
  | 'paginationRange'
  | 'paginationEmpty'
  | 'selectFilter'
  // WP-FU1: tiedostonvalinnan tekstit. `fileChoose` ja `fileNone` renderöityvät
  // PALVELIMELLA (`FileUploadRenderer`), joten ne ovat myös `AppMessages`in
  // palvelinkatalogissa; `fileSkipped` on runtimen kirjoittama virheteksti.
  // Ennen tätä napin teksti tuli selaimelta eikä sitä voinut kääntää millään.
  | 'fileChoose'
  | 'fileNone'
  | 'fileSkipped'
  // WP-UA4: `user-actions`-komponentin oma kromi. Kortit piirtyvät selaimessa
  // datan saavuttua, joten nämä eivät ole palvelimen renderöimiä tekstejä.
  | 'userActionRespond'
  | 'userActionComment'
  | 'userActionEmpty'
  | 'userActionAnswered'
  | 'userActionDue'
  | 'userActionOverdue'
  | 'userActionSignIn'
  | 'userActionFailed'
  | 'userActionChoiceRequired'
  // user-action-await-plan.md: odottavan prosessin jatko (user-action:await) kortissa.
  | 'userActionWaiting'
  | 'userActionContinuationFailed'
  // WP-VV2/VV6 (palvelinvirheet-kayttajalle-plan.md, D1 = B): tekstit jotka
  // näytetään palvelimen oman 4xx-viestin puuttuessa. `serverError` = 5xx,
  // `requestFailed` = kutsu ei saanut vastausta (syy jää Debug-aikajanaan),
  // `loadFailed` = taulun/kaavion haku, `moveFailed` = kanbanin siirto.
  | 'serverError'
  | 'requestFailed'
  | 'loadFailed'
  | 'moveFailed'
  // WP-RP5 (repeater-parannukset-plan.md, D7 = A): repeaterin napit Appin kielellä,
  // kun rakentaja ei ole antanut omaa tekstiä. `repeaterAdd` ja `repeaterRemove`
  // renderöityvät palvelimella (AppMessages); `repeaterRemoveRow` on poistonapin
  // saavutettava nimi rivinumerolla.
  | 'repeaterAdd'
  | 'repeaterRemove'
  | 'repeaterRemoveRow';

/** Appin kieli (`metadata.locale`). `D5` = A: oletus on `en`. */
export type MessageLocale = 'en' | 'fi';

/**
 * Sanaston AINOA lista — validaattori (`AppContentValidator`) hyväksyy
 * `metadata.messages`iin vain nämä avaimet, jotta kirjoitusvirhe ei jää
 * hiljaiseksi no-opiksi.
 */
export const MESSAGE_KEYS: readonly MessageKey[] = [
  'required', 'notANumber', 'min', 'max', 'minLength', 'maxLength',
  'regex', 'email', 'invalid', 'loading',
  'booleanTrue', 'booleanFalse', 'paginationRange', 'paginationEmpty', 'selectFilter',
  'fileChoose', 'fileNone', 'fileSkipped',
  'userActionRespond', 'userActionComment', 'userActionEmpty', 'userActionAnswered',
  'userActionDue', 'userActionOverdue', 'userActionSignIn', 'userActionFailed',
  'userActionChoiceRequired', 'userActionWaiting', 'userActionContinuationFailed',
  'serverError', 'requestFailed', 'loadFailed', 'moveFailed',
  'repeaterAdd', 'repeaterRemove', 'repeaterRemoveRow',
];

export const MESSAGE_LOCALES: readonly MessageLocale[] = ['en', 'fi'];

/** `{value}` korvautuu sääntöparametrilla (raja tai pituus). */
type Catalog = Record<MessageKey, string>;

const EN: Catalog = {
  required: 'Required',
  notANumber: 'Must be a number',
  min: 'Min {value}',
  max: 'Max {value}',
  minLength: 'At least {value} characters',
  maxLength: 'At most {value} characters',
  regex: 'Invalid format',
  email: 'Enter a valid email address',
  invalid: 'Invalid value',
  loading: 'Loading…',
  booleanTrue: 'Yes',
  booleanFalse: 'No',
  paginationRange: '{start}–{end} of {total}',
  paginationEmpty: '0 of 0',
  selectFilter: 'Filter options…',
  fileChoose: 'Choose file',
  fileNone: 'No file chosen',
  fileSkipped: 'Skipped (over {value} MB): {names}',
  userActionRespond: 'Respond',
  userActionComment: 'Comment',
  userActionEmpty: 'No actions for you right now',
  userActionAnswered: 'Answered',
  userActionDue: 'Due {value}',
  userActionOverdue: 'Past due',
  userActionSignIn: 'Sign in to see the actions assigned to you',
  userActionFailed: 'Could not load actions',
  userActionChoiceRequired: 'Choose an option',
  userActionWaiting: 'Waiting process: route {value} continues from the answer',
  userActionContinuationFailed: 'The process could not continue: {value}',
  serverError: 'The service ran into an error. Please try again later.',
  requestFailed: 'The request could not be completed. Please try again.',
  loadFailed: 'Could not load the data',
  moveFailed: 'The move could not be saved ({value})',
  repeaterAdd: 'Add row',
  repeaterRemove: 'Remove',
  repeaterRemoveRow: 'Remove row {value}',
};

const FI: Catalog = {
  required: 'Pakollinen tieto',
  notANumber: 'Anna luku',
  min: 'Vähintään {value}',
  max: 'Enintään {value}',
  minLength: 'Vähintään {value} merkkiä',
  maxLength: 'Enintään {value} merkkiä',
  regex: 'Tarkista muoto',
  email: 'Anna kelvollinen sähköpostiosoite',
  invalid: 'Tarkista arvo',
  loading: 'Ladataan…',
  booleanTrue: 'Kyllä',
  booleanFalse: 'Ei',
  paginationRange: '{start}–{end} / {total}',
  paginationEmpty: '0 / 0',
  selectFilter: 'Suodata vaihtoehtoja…',
  fileChoose: 'Valitse tiedosto',
  fileNone: 'Ei valittua tiedostoa',
  fileSkipped: 'Ohitettu (yli {value} MB): {names}',
  userActionRespond: 'Vastaa',
  userActionComment: 'Kommentti',
  userActionEmpty: 'Ei sinulle osoitettuja toimia',
  userActionAnswered: 'Vastattu',
  userActionDue: 'Määräaika {value}',
  userActionOverdue: 'Määräaika ohi',
  userActionSignIn: 'Kirjaudu sisään nähdäksesi sinulle osoitetut toimet',
  userActionFailed: 'Toimien haku epäonnistui',
  userActionChoiceRequired: 'Valitse vaihtoehto',
  userActionWaiting: 'Odottava prosessi: reitti {value} jatkaa vastauksesta',
  userActionContinuationFailed: 'Prosessin jatkoa ei voitu ajaa: {value}',
  serverError: 'Palvelussa tapahtui virhe. Yritä myöhemmin uudelleen.',
  requestFailed: 'Pyyntöä ei saatu käsiteltyä. Yritä uudelleen.',
  loadFailed: 'Tietojen haku epäonnistui',
  moveFailed: 'Siirtoa ei saatu tallennettua ({value})',
  repeaterAdd: 'Lisää rivi',
  repeaterRemove: 'Poista',
  repeaterRemoveRow: 'Poista rivi {value}',
};

const CATALOGS: Record<MessageLocale, Catalog> = { en: EN, fi: FI };

/** Palvelin lukee saman katalogin omiin renderöimiinsä teksteihin (WP-VM2). */
export function messageCatalog(locale: MessageLocale): Readonly<Catalog> {
  return CATALOGS[locale] ?? EN;
}

export function isMessageKey(value: unknown): value is MessageKey {
  return typeof value === 'string' && (MESSAGE_KEYS as readonly string[]).includes(value);
}

export function isMessageLocale(value: unknown): value is MessageLocale {
  return typeof value === 'string' && (MESSAGE_LOCALES as readonly string[]).includes(value);
}

let activeLocale: MessageLocale = 'en';
let overrides: Partial<Record<MessageKey, string>> = {};

/**
 * Ota Appin kieli ja ohitukset käyttöön. Runtime kutsuu tätä bootissa
 * `window.fasthub.appMessages`-siemenestä; testit kutsuvat suoraan.
 *
 * <p>Tuntematon kieli tai avain OHITETAAN hiljaa täällä — palvelinvalidointi on
 * se kerros joka hylkää ne, eikä ajossa ole hyötyä kaatua sisällöstä joka on jo
 * julkaistu.
 */
export function configureMessages(config?: {
  locale?: unknown;
  overrides?: Record<string, unknown> | null;
}): void {
  activeLocale = isMessageLocale(config?.locale) ? config!.locale as MessageLocale : 'en';
  const collected: Partial<Record<MessageKey, string>> = {};
  const given = config?.overrides;
  if (given && typeof given === 'object') {
    for (const [key, value] of Object.entries(given)) {
      if (isMessageKey(key) && typeof value === 'string' && value !== '') {
        collected[key] = value;
      }
    }
  }
  overrides = collected;
}

/**
 * Appin voimassa oleva kieli. Muotoilijat (`stat.ts`, `dataGrid.ts`,
 * `pagination.ts`) pysyvät puhtaina ja ottavat kielen parametrina — runtime lukee
 * sen tästä ja välittää. Näin testit eivät riipu moduulitilan järjestyksestä.
 */
export function currentLocale(): MessageLocale {
  return activeLocale;
}

/** Nollaa tilan (testit; myös uudelleenboottaava runtime). */
export function resetMessages(): void {
  activeLocale = 'en';
  overrides = {};
}

/**
 * Sisäänrakennettu teksti: ohitus jos on, muuten aktiivisen kielen katalogi.
 * `param` korvaa `{value}`-paikkamerkin (esim. `Min 0` / `Vähintään 0`).
 */
export function resolveMessage(key: MessageKey, param?: unknown): string {
  const template = overrides[key] ?? messageCatalog(activeLocale)[key] ?? EN[key];
  if (param === undefined || param === null) {
    return template;
  }
  return template.replace('{value}', String(param));
}

/**
 * Päivämäärävälin esiasetusten näkyvät tekstit (WP-SF6, löydös T-F).
 *
 * <p>Nämä renderöityvät PALVELIMELLA (`DateRangePickerRenderer` kirjoittaa
 * `<select>`in optiot), joten Java-puolella on sama taulu — ja
 * `AppBuilderMessageCatalogTest` pinnaa ne toisiinsa, kuten `loading`in.
 *
 * <p>Nämä EIVÄT ole `metadata.messages`-ylikirjoitettavia: kyse on komponentin
 * kromista, ei Appin sisällöstä. Ennen tätä ne olivat kovakoodattua englantia,
 * jota suomenkielisessä Appissa ei saanut käännettyä millään — ainoa pakotie
 * oli `showPresets: false`, eli koko ominaisuus pois.
 */
export const PRESET_LABELS: Record<MessageLocale, Record<string, string>> = {
  en: {
    '': 'Custom',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisQuarter: 'This quarter',
    lastQuarter: 'Last quarter',
    yearToDate: 'Year to date',
  },
  fi: {
    '': 'Mukautettu',
    today: 'Tänään',
    yesterday: 'Eilen',
    thisWeek: 'Tämä viikko',
    lastWeek: 'Viime viikko',
    last7days: 'Viimeiset 7 päivää',
    last30days: 'Viimeiset 30 päivää',
    thisMonth: 'Tämä kuukausi',
    lastMonth: 'Viime kuukausi',
    thisQuarter: 'Tämä vuosineljännes',
    lastQuarter: 'Viime vuosineljännes',
    yearToDate: 'Vuoden alusta',
  },
};
